import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrackingStops,
  createTrackingStop,
  updateTrackingStop,
  deleteTrackingStop,
  type TrackingStop,
  type StopUpsertPayload,
} from "../services/tracking/stopsService";

const KEY = "tracking-stops";

export function useTrackingStops() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<TrackingStop[]>(
    token ? [KEY, token] : null,
    () => fetchTrackingStops(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);
  const list = Array.isArray(data) ? data : [];

  const create = useCallback(
    async (payload: StopUpsertPayload) => {
      const created = await createTrackingStop(token, payload);
      await mutate();
      return created;
    },
    [token, mutate]
  );

  const update = useCallback(
    async (id: string, payload: StopUpsertPayload) => {
      const updated = await updateTrackingStop(token, id, payload);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteTrackingStop(token, id);
      await mutate();
    },
    [token, mutate]
  );

  return {
    stops: list,
    isLoading,
    error,
    refetch,
    create,
    update,
    remove,
    accessToken: token,
  };
}

export type { TrackingStop, StopUpsertPayload };
